import { useCallback, useState } from "react"
import { LoginState } from "../../../enums/login/login-state"
import { FilledButton } from "../common/buttons/FilledButton"
import LoginFormStructure from "./LoginFormStructure"
import LoginInput from "./LoginInput"
import { useDispatch } from "react-redux"
import { AppDispatch } from "../../../redux/store"
import { setUser } from '../../../redux/slices/session'
import { centsClient } from "../../../api/cents/clients/cents-client"
import React from "react"
import { RecoveryPasswordForm } from "./recover/RecoverPasswordForm"
import { translations } from "../../../utils/translations/translations-manager"

type LoginError = 'invalid_credentials' | 'verify_credentials' | 'user_authentication_not_supported' | 'recover_password_error'
const errorMessages: Record<LoginError, () => string> = {
  'invalid_credentials': () => translations.texts.invalid_credentials.toString(),
  'verify_credentials': () => translations.texts.verify_credentials.toString(),
  'user_authentication_not_supported': () => translations.texts.authentication_not_supported.toString(),
  'recover_password_error': () => translations.texts.recover_password_error.toString()
}

export function LoginForm({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const dispatch: AppDispatch = useDispatch()
  const [state, setState] = useState<LoginState>(LoginState.usernameInput)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<LoginError | undefined>()
  const [username, setUsername] = useState<string | undefined>()
  const [password, setPassword] = useState<string | undefined>()
  const [otp, setOtp] = useState<string | undefined>()
  const [otpTimeout, setOtpTimeout] = useState<boolean>(false)

  const onError = useCallback((error: LoginError) => {
    setPassword(undefined)
    setError(error)
  }, [])

  const generateOTP = useCallback(
    async () => {
      if (!username) {
        onError('verify_credentials')
        return
      }
      setOtpTimeout(true)
      await centsClient.auth.generateOtp(username)
      // send request to generate another OTP
      setTimeout(() => setOtpTimeout(false), 60000)
    },
    [username, onError],
  )

  const initiate = useCallback(async () => {
    if (!username) {
      onError('verify_credentials')
      return
    }
    setLoading(true)
    let res
    try {
      res = await centsClient.auth.initiate(username)
      setLoading(false)
    } catch {
      onError('invalid_credentials')
      setLoading(false)
      return
    }
    if (res.methods.includes('password')) {
      setState(LoginState.passwordInput)
    } else if (res.methods.includes('otp')) {
      generateOTP()
      setState(LoginState.otpInput)
    } else {
      onError('user_authentication_not_supported')
      return
    }
  }, [username, generateOTP, onError])

  const loginByPassword = useCallback(
    () => {
      if (!username || !password) {
        onError('verify_credentials')
        return
      }
      login({ username, password })
    },
    [username, password, onError],
  )

  const loginByOtp = useCallback(
    () => {
      if (!username || !otp) {
        onError('verify_credentials')
        return
      }
      login({ username, otp })
    },
    [username, otp, onError],
  )

  const login = useCallback(
    async (credentials: { username: string, password?: string, otp?: string }) => {
      if (!credentials.username || (!credentials.password && !credentials.otp)) {
        onError('verify_credentials')
        return
      }
      setLoading(true)
      try {
        if (credentials.password) await centsClient.auth.loginByPassword(credentials.username, credentials.password)
        else if (credentials.otp) await centsClient.auth.loginByOtp(credentials.username, credentials.otp)
        setLoading(false)
      } catch {
        setLoading(false)
        // TODO: Handle error
        onError('invalid_credentials')
        return
      }
      // TODO: Set auth token in apis
      setState(LoginState.success)
      dispatch(setUser(credentials.username))
      onSuccess()
    },
    [dispatch, onError],
  )

  const resetLogin = useCallback(
    () => {
      setError(undefined)
      setState(LoginState.usernameInput)
    },
    [],
  )

  // TODO: That should be improved, rendering from an hook is not the best practice

  if (error) {
    return (
      <>
        <p className="text-red-700 font-medium">{errorMessages[error]()}</p>
        <FilledButton label={translations.texts.retry.toString()} onClick={resetLogin} className="min-h-10 w-full" />
      </>
    )
  }
  switch (state) {
    case LoginState.usernameInput:
      return (
        <>
          <LoginFormStructure
            title={translations.texts.login_with_email_or_password.toString()}
            handleSubmit={initiate}>
            <>
              <LoginInput name={'username'} value={username} setValue={setUsername} />
              <FilledButton label={translations.texts.continue.toString()} loading={loading} className="min-h-10 w-full" type="submit" />
            </>
          </LoginFormStructure>
          <p onClick={() => { setState(LoginState.passwordRecovery) }} className="truncate cursor-pointer overflow-hidden underline text-blue-700">{translations.texts.recover_password.toString()}</p>
        </>)
    case LoginState.passwordInput:
      return (<>
        <LoginFormStructure
          title={translations.texts.insert_password.toString()}
          handleSubmit={loginByPassword}>
          <>
            <LoginInput name={'password'} value={password} setValue={setPassword} isPassword />
            <FilledButton label={translations.texts.login.toString()} loading={loading} className="min-h-10 w-full" type="submit" />
          </>
        </LoginFormStructure>
        <p onClick={() => { setPassword(undefined); resetLogin() }} className="truncate cursor-pointer overflow-hidden underline text-blue-700">{translations.texts.back_to_login.toString()}</p>
      </>
      )

    case LoginState.otpInput:
      return (
        <>
          <LoginFormStructure
            title={translations.texts.insert_otp.toString()}
            handleSubmit={loginByOtp}>
            <>
              <LoginInput name={'otp'} value={otp} setValue={setOtp} />
              <div className="flex gap-3">
                <FilledButton label={"Verifica"} loading={loading} className="min-h-10 w-full" type="submit" />
                <FilledButton disabled={otpTimeout} onClick={generateOTP} label={"Rimanda"} className="min-h-10 w-full border-2 border-cents-purple !bg-cents-white !text-cents-purple" />
              </div>
            </>
          </LoginFormStructure>
          <p onClick={() => { setOtp(undefined); resetLogin() }} className="truncate cursor-pointer overflow-hidden underline text-blue-700">{translations.texts.back_to_login.toString()}</p>
        </>
      )
    case LoginState.passwordRecovery:
      return (
        <RecoveryPasswordForm initialUsername={username} onConfirm={(value) => {
          setUsername(value)
          setState(LoginState.usernameInput)
        }}
          onCancel={() => setState(LoginState.usernameInput)}
          onError={onError}
        />
      )
    default:
      return <></>
  }
}
